import { AlertTriangle, CheckCircle2, ChevronDown, Hourglass, MinusCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { RUN_STATUS_LABEL, RUN_TRIGGER_LABEL } from '../lib/automation';
import { formatDateTime } from '../lib/format';
import type { AutomationRun, AutomationRunStatus, StepResult, TraceNode } from '../lib/types';
import { describeCondition, describeValue, type ConditionOptions } from './ConditionBuilder';
import { Badge, cn } from './ui';

type Tone = Parameters<typeof Badge>[0]['tone'];

const RUN_TONE: Record<AutomationRunStatus, Tone> = {
  RUNNING: 'blue',
  SUCCEEDED: 'green',
  SKIPPED: 'gray',
  FAILED: 'red',
  WAITING: 'amber',
  CANCELED: 'gray',
};

export function RunStatusBadge({ status }: { status: AutomationRunStatus }) {
  return <Badge tone={RUN_TONE[status]}>{RUN_STATUS_LABEL[status]}</Badge>;
}

const ResultIcon = ({ ok }: { ok: boolean }) =>
  ok ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 size-4 shrink-0 text-slate-400" />;

/** Which conditions matched, and what the ticket actually had. */
export function TraceView({ node, options }: { node: TraceNode; options: ConditionOptions }) {
  if (node.type === 'condition') {
    return (
      <div className="flex items-start gap-2 text-sm">
        <ResultIcon ok={node.result} />
        <span className="min-w-0 text-slate-700">
          {describeCondition(node, options)}
          <span className="block text-xs text-slate-500">This ticket: {describeValue(node.field, node.actual, options)}</span>
        </span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-2 text-sm font-medium text-slate-800">
        <ResultIcon ok={node.result} />
        {node.children?.length ? (node.match === 'all' ? 'All of these' : 'Any of these') : 'No conditions (every ticket matches)'}
      </div>
      {!!node.children?.length && (
        <div className="space-y-1.5 border-l border-slate-200 pl-4">
          {node.children.map((child, index) => (
            <TraceView key={index} node={child} options={options} />
          ))}
        </div>
      )}
    </div>
  );
}

const STEP_ICON: Record<StepResult['status'], React.ReactNode> = {
  done: <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />,
  skipped: <MinusCircle className="mt-0.5 size-4 shrink-0 text-slate-400" />,
  failed: <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />,
  waiting: <Hourglass className="mt-0.5 size-4 shrink-0 text-amber-600" />,
};

export function StepList({ steps }: { steps: StepResult[] }) {
  return (
    <ul className="space-y-1.5">
      {steps.map((step, index) => (
        <li key={index} className={cn('flex items-start gap-2 text-sm', step.status === 'failed' ? 'text-red-800' : 'text-slate-700')}>
          {STEP_ICON[step.status]}
          <span className="min-w-0">{step.summary}</span>
        </li>
      ))}
    </ul>
  );
}

/** Run history rows that expand to show each step and the condition results. */
export function RunList({ runs, options, showName = true }: { runs: AutomationRun[]; options: ConditionOptions; showName?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <ul className="divide-y divide-slate-100">
      {runs.map((run) => {
        const expanded = open === run.id;
        const trigger = RUN_TRIGGER_LABEL[run.trigger] ?? run.trigger;
        return (
          <li key={run.id}>
            <button
              type="button"
              onClick={() => setOpen(expanded ? null : run.id)}
              className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-slate-50"
              aria-expanded={expanded}
            >
              <RunStatusBadge status={run.status} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {showName ? run.name : trigger}
                  {run.workOrder && (
                    <span className="font-normal text-slate-500">
                      {' '}
                      · #{run.workOrder.number} {run.workOrder.title}
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {formatDateTime(run.startedAt)}
                  {showName && ` · ${trigger}`}
                  {run.message && ` · ${run.message}`}
                </p>
              </div>
              <ChevronDown className={cn('mt-1 size-4 shrink-0 text-slate-400 transition-transform', expanded && 'rotate-180')} />
            </button>
            {expanded && (
              <div className="space-y-3 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
                {run.workOrder && (
                  <Link to={`/work-orders/${run.workOrder.id}`} className="text-sm font-medium text-brand-700 hover:underline">
                    Open ticket #{run.workOrder.number}
                  </Link>
                )}
                {run.status === 'WAITING' && run.resumeAt && <p className="text-sm text-amber-800">Continues {formatDateTime(run.resumeAt)}</p>}
                {run.steps.length > 0 ? <StepList steps={run.steps} /> : <p className="text-sm text-slate-500">No actions ran.</p>}
                {run.conditionTrace && (
                  <div>
                    <p className="mb-1.5 text-xs font-semibold tracking-wide text-slate-500 uppercase">Conditions</p>
                    <TraceView node={run.conditionTrace} options={options} />
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
